import type {
  DatasetsMutatorItemPayload,
  DatasetsMutatorItems,
  DatasetsMutatorItemsStatus,
  DatasetsMutatorRemoveItemProps,
} from "./datasets-mutator.type";

import { atom } from "jotai";
import { atomWithReset } from "jotai/utils";
import omit from "lodash/omit";

import {
  convertDatasetsMutatorItem,
  getDatasetsMutatorItemKey,
  getDatasetsMutatorRemoveItemKey,
} from "./datasets-mutator.helper";

/**
 * Collection of mutator items
 *
 * A mutator item represents a dataset mutation to come (create, update or delete).
 *
 * On each user actions to a dataset, a mutator item is added to the collection.
 * Each item is removed from the collection just after his mutation has finished.
 */
export const datasetsMutatorItemsAtom = atomWithReset<DatasetsMutatorItems>({});

/**
 * Collection of mutations status
 *
 * A mutation status is the status returned by React Query `useMutation()`.
 *
 * On each mutation status change, the status is sync with the collection.
 * The status collection is resetted when the user click save/cancel buttons.
 */
export const datasetsMutatorItemsStatusAtom =
  atomWithReset<DatasetsMutatorItemsStatus>({});

/**
 * Start mutate is the on/off toggle to execute mutations stored in `datasetsMutatorItemsAtom`.
 */
export const datasetsMutatorStartMutateAtom = atom(false);

/**
 * Derived readable atoms
 */

export const datasetsMutatorIsDirtyAtom = atom((get) => {
  return Object.keys(get(datasetsMutatorItemsAtom)).length > 0;
});

export const datasetsMutatorIsSavingAtom = atom((get) => {
  const itemsStatus = Object.values(get(datasetsMutatorItemsStatusAtom));
  return itemsStatus.includes("pending");
});

export const datasetsMutatorIsFinishedAtom = atom((get) => {
  const itemsStatus = Object.values(get(datasetsMutatorItemsStatusAtom));
  return itemsStatus.length > 0 && !itemsStatus.includes("pending");
});

/**
 * Derived writable atoms
 */

export const datasetsMutatorAddItemAtom = atom(
  null,
  (get, set, item: DatasetsMutatorItemPayload) => {
    const items = get(datasetsMutatorItemsAtom);
    const itemData = convertDatasetsMutatorItem(item);
    const mutationKey = getDatasetsMutatorItemKey(itemData);
    set(datasetsMutatorItemsAtom, {
      ...items,
      [mutationKey]: { ...itemData, mutationKey },
    });
  }
);

export const datasetsMutatorRemoveItemAtom = atom(
  null,
  (get, set, props: DatasetsMutatorRemoveItemProps) => {
    const items = get(datasetsMutatorItemsAtom);
    const mutationKey = getDatasetsMutatorRemoveItemKey(props);
    const newAccumulator = omit(items, [mutationKey]);
    set(datasetsMutatorItemsAtom, newAccumulator);
  }
);
